import { useMemo } from "react";

import moment from "moment";

import { useUser } from "@components/user-context";
import { formatting } from "@north-beam/nb-common";

export const OrderTable = ({ orderData }: { orderData: any }) => {
  const { user } = useUser();
  const formatCurrency = (value: number | undefined) => {
    if (!value) {
      return "N/A";
    }
    return formatting.formatMetric.exact(value, "dollars", {
      code: user.currencies.currentCurrency,
    });
  };

  const formattedDate = useMemo(() => {
    const date = orderData?.me.order.occurredAt.value;
    if (date) {
      return moment(date).format("MMM DD, YYYY, h:ma");
    }

    return "";
  }, [orderData]);

  return (
    <div className="overflow-auto rounded border border-solid border-v2-secondary max-sm:rounded-sm">
      <TableHeader />
      <div>
        <OrderRow
          type="Date"
          details={formattedDate}
          isAlternate={false}
          valueCellClassName="text-right"
        />
        <OrderRow
          type="Amount"
          details={
            <>
              <span>
                {formatCurrency(orderData?.me?.order?.revenueInDollars)}
              </span>
            </>
          }
          isAlternate={true}
          valueCellClassName="text-right"
        />
        <OrderRow
          type="Discount"
          details={formatCurrency(orderData?.me?.order?.discountValue)}
          isAlternate={false}
          valueCellClassName="text-right"
        />
        <OrderRow
          type="Shipping"
          details={formatCurrency(orderData?.me?.order?.shippingValue)}
          isAlternate={true}
          valueCellClassName="text-right"
        />
        <OrderRow
          type="Tax"
          details={formatCurrency(orderData?.me?.order?.taxValue)}
          isAlternate={false}
          valueCellClassName="text-right"
        />
        <OrderRow
          type="Refund"
          details={formatCurrency(orderData?.me?.order?.refundAmountInDollars)}
          isAlternate={true}
          valueCellClassName="text-right"
        />
        <OrderRow
          type="Subscription"
          details={orderData?.me?.order?.subscriptionType || "-"}
          isAlternate={false}
          valueCellClassName="text-right"
        />
        <OrderRow
          type="Product(s)"
          details={
            <div className="flex flex-col gap-2">
              {orderData?.me?.order?.products
                ? orderData.me.order.products.map(
                    (product: any, index: number) => (
                      <div key={index} className="flex justify-between gap-2">
                        <span className="text-left align-left font-sans">
                          {product.title}
                        </span>
                        <div className="flex gap-2">
                          <span>{product.quantity}</span>
                          <span>×</span>
                          <span>{formatCurrency(product.priceInDollars)}</span>
                        </div>
                      </div>
                    ),
                  )
                : "-"}
            </div>
          }
          isAlternate={true}
          valueCellClassName="text-right"
          isLast={true}
        />
      </div>
    </div>
  );
};

export const TableHeader = () => {
  return (
    <div className="flex border-b border-solid border-b-v2-secondary sticky top-0">
      <div
        className="px-4 py-1.5 text-xs font-bold
      tracking-wide leading-5 uppercase bg-v2-primary border-r border-solid border-r-v2-secondary text-v2-primary w-[113px]"
      >
        TYPE
      </div>
      <div className="flex-1 px-4 py-1.5 text-xs font-bold tracking-wide leading-5 uppercase bg-v2-primary text-v2-primary">
        DETAILS
      </div>
    </div>
  );
};

interface OrderRowProps {
  type: string;
  details: React.ReactNode;
  isAlternate?: boolean;
  isLast?: boolean;
  valueCellClassName?: string;
}

export const OrderRow: React.FC<OrderRowProps> = ({
  type,
  details,
  isAlternate = false,
  isLast = false,
  valueCellClassName = "",
}) => {
  const rowClassName = `
    flex ${isAlternate ? "bg-v2-secondary" : "bg-v2-primary"}
  `;
  const borderClassName = isLast
    ? ""
    : "border-b border-solid border-b-v2-secondary";

  return (
    <div className={rowClassName}>
      <div
        className={`px-4 py-2.5 text-xs font-medium tracking-wide leading-5 border-r border-solid border-r-v2-secondary text-v2-primary w-[113px] max-sm:px-3 max-sm:py-2 max-sm:w-[100px] ${borderClassName} font-sans`}
      >
        {type}
      </div>
      <div
        className={`flex-1 px-4 py-2.5 text-xs leading-4 text-v2-primary max-sm:px-3 max-sm:py-2 ${borderClassName} font-mono ${valueCellClassName}`}
      >
        {details}
      </div>
    </div>
  );
};
